import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";

import {
  Box,
  Grid,
  Toolbar,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Pagination,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";

import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import AddShoppingCartRounded from "@mui/icons-material/AddShoppingCartRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import { CloudDownloadOutlined, KeyboardArrowLeftOutlined } from "@mui/icons-material";

import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";

function CriticalStockReport() {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

  const [rawMaterials, setRawMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchText, setSearchText] = useState("");

  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);

  const getRawMaterialsList = async () => {
    try {
      if (!outletData?._id) return;

      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletCurrentStock?outletId=${outletData._id}&limit=100`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setCategories(response?.data?.data?.rawMaterialCategories || []);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const getStockTransactions = async () => {
    if (!outletData?._id) return;

    setLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.mainCategory = selectedCategory;

      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getCriticalStockReportForOutlet?outletId=${outletData._id}&limit=200`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        }
      );

      const report = response?.data?.data?.report;

      if (typeof report === "string" && report.includes("No Records")) {
        setRawMaterials([]);
      } else {
        setRawMaterials(Array.isArray(report) ? report : []);
      }

      setCurrentPage(1);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to fetch report", { autoClose: 3000 });
      setRawMaterials([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRawMaterialsList();
    getStockTransactions();
  }, []);

  const uniqueCategories = useMemo(() => {
    return (categories || []).filter(
      (category, index, self) =>
        category?.mainCategory?._id &&
        index === self.findIndex((c) => c?.mainCategory?._id === category?.mainCategory?._id)
    );
  }, [categories]);

  const filteredRows = useMemo(() => {
    const rows = Array.isArray(rawMaterials) ? rawMaterials : [];
    const q = searchText.trim().toLowerCase();

    if (!q) return rows;

    return rows.filter((r) => {
      const name = (r?.rawMaterial?.name || "").toLowerCase();
      const cat = (r?.rawMaterial?.mainCategory?.name || "").toLowerCase();
      const sub = (r?.rawMaterial?.subCategory?.name || "").toLowerCase();
      const remark = (r?.remark || "").toLowerCase();
      const unit = (r?.rawMaterial?.unit || "").toLowerCase();

      return (
        name.includes(q) ||
        cat.includes(q) ||
        sub.includes(q) ||
        remark.includes(q) ||
        unit.includes(q)
      );
    });
  }, [rawMaterials, searchText]);

  const summary = useMemo(() => {
    const rows = Array.isArray(filteredRows) ? filteredRows : [];
    const outOfStock = rows.filter((r) => Number(r?.inStock || 0) <= 0).length;
    const lowStock = rows.filter((r) => Number(r?.inStock || 0) > 0).length;

    return {
      total: rows.length,
      outOfStock,
      lowStock,
    };
  }, [filteredRows]);

  const totalPages = Math.ceil((filteredRows?.length || 0) / itemsPerPage) || 1;

  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const selectedCategoryName = useMemo(() => {
    if (!selectedCategory) return "All Categories";

    const found = uniqueCategories.find((c) => c?.mainCategory?._id === selectedCategory);
    return found?.mainCategory?.name || "Selected Category";
  }, [selectedCategory, uniqueCategories]);

  const handlePageChange = (event, page) => setCurrentPage(page);

  const handleResetFilters = () => {
    setSelectedCategory("");
    setSearchText("");
    setCurrentPage(1);
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const title = "Critical Items Stock Report";
    doc.setFontSize(16);
    doc.text(title, pageWidth / 2, 18, { align: "center" });

    doc.setFontSize(10);
    const outletName = outletData?.name || "Unknown Outlet";
    const outletAddress = outletData?.address || "No Address Available";
    const outletContact = `${outletData?.email || "No Email"}, ${outletData?.phone || "No Phone"}`;

    doc.text(`Outlet: ${outletName}`, 14, 30);
    doc.text(`Address: ${outletAddress}`, 14, 38);
    doc.text(`Contact: ${outletContact}`, 14, 46);
    doc.text(`Category: ${selectedCategoryName}`, 14, 54);

    const columns = [
      { header: "#", dataKey: "itemNo" },
      { header: "Raw Material", dataKey: "productName" },
      { header: "Category", dataKey: "category" },
      { header: "Sub Category", dataKey: "subcategory" },
      { header: "Stock", dataKey: "stock" },
      { header: "Unit", dataKey: "unit" },
      { header: "Remark", dataKey: "remark" },
      { header: "Status", dataKey: "status" },
    ];

    const sortedMaterials = [...filteredRows].sort((a, b) => {
      const ca = (a?.rawMaterial?.mainCategory?.name || "").toLowerCase();
      const cb = (b?.rawMaterial?.mainCategory?.name || "").toLowerCase();
      const sa = (a?.rawMaterial?.subCategory?.name || "").toLowerCase();
      const sb = (b?.rawMaterial?.subCategory?.name || "").toLowerCase();
      const na = (a?.rawMaterial?.name || "").toLowerCase();
      const nb = (b?.rawMaterial?.name || "").toLowerCase();

      if (ca !== cb) return ca.localeCompare(cb);
      if (sa !== sb) return sa.localeCompare(sb);
      return na.localeCompare(nb);
    });

    const rows = sortedMaterials.map((m, index) => {
      const stock = Number(m?.inStock || 0);

      return {
        itemNo: index + 1,
        productName: m?.rawMaterial?.name || "N/A",
        category: m?.rawMaterial?.mainCategory?.name || "N/A",
        subcategory: m?.rawMaterial?.subCategory?.name || "N/A",
        stock: stock.toFixed(2),
        unit: m?.rawMaterial?.unit || "N/A",
        remark: m?.remark || "-",
        status: stock <= 0 ? "Out of Stock" : "Low Stock",
      };
    });

    doc.autoTable({
      columns,
      body: rows,
      startY: 62,
      theme: "grid",
      styles: { halign: "center", fontSize: 9 },
      headStyles: {
        fillColor: [0, 48, 73],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 40, halign: "left" },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 16 },
        5: { cellWidth: 14 },
        6: { cellWidth: 32, halign: "left" },
        7: { cellWidth: 22 },
      },
    });

    const today = new Date().toISOString().split("T")[0];
    doc.save(`${today}-Critical-Stock-Report.pdf`);
  };

  const handleRaiseStockRequest = () => {
    const criticalItems = filteredRows
      .filter((m) => m?.rawMaterial?._id)
      .map((m) => ({
        rawMaterial: m.rawMaterial,
        requestedQuantity: 1,
        inStock: m?.inStock || 0,
        remark: m?.remark || "",
      }));

    if (!criticalItems.length) {
      toast.error("No critical stock items available.");
      return;
    }

    navigate("/stockRequest", {
      state: {
        mode: "critical-stock",
        criticalItems,
      },
    });
  };

  const StatCard = ({ icon, title, value, helper, bg, color }) => (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid #e6eaf0",
        background: bg,
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#fff",
              color,
              boxShadow: "0 8px 20px rgba(15, 23, 42, 0.08)",
            }}
          >
            {icon}
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#64748b" }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 900, color: "#0f172a", lineHeight: 1.1 }}>
              {value}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.3 }}>
              {helper}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
      <Toolbar />

      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={11}>
          <Card
            elevation={0}
            sx={{
              mt: 4,
              borderRadius: 4,
              overflow: "hidden",
              border: "1px solid #dbeafe",
              background:
                "radial-gradient(circle at top right, rgba(22, 163, 74, 0.18), transparent 30%), linear-gradient(135deg, #003049 0%, #075985 100%)",
              color: "#fff",
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <IconButton
                    onClick={() => navigate(-1)}
                    sx={{
                      color: "#fff",
                      backgroundColor: "rgba(255,255,255,0.12)",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
                    }}
                  >
                    <KeyboardArrowLeftOutlined />
                  </IconButton>

                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 900 }}>
                        Critical Items Stock Report
                      </Typography>

                      <Chip
                        size="small"
                        label={selectedCategoryName}
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.16)",
                          color: "#fff",
                          fontWeight: 800,
                          "& .MuiChip-label": { color: "#fff" },
                        }}
                      />
                    </Stack>

                    <Typography sx={{ mt: 0.7, fontSize: 13, color: "#dbeafe", maxWidth: 650 }}>
                      Review critical stock items, filter category-wise, download report, or directly raise a stock request.
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  variant="contained"
                  startIcon={<AddShoppingCartRounded />}
                  disabled={loading || filteredRows.length === 0}
                  onClick={handleRaiseStockRequest}
                  sx={{
                    height: 46,
                    px: 2.5,
                    borderRadius: 2.5,
                    fontWeight: 900,
                    textTransform: "none",
                    backgroundColor: "#16a34a",
                    boxShadow: "0 10px 24px rgba(22, 163, 74, 0.35)",
                    "&:hover": { backgroundColor: "#15803d" },
                    "&.Mui-disabled": {
                      backgroundColor: "rgba(255,255,255,0.25)",
                      color: "rgba(255,255,255,0.65)",
                    },
                  }}
                >
                  Raise Request From Report
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={4}>
              <StatCard
                icon={<Inventory2Outlined />}
                title="Total Critical Items"
                value={summary.total}
                helper="Based on selected filters"
                bg="#ffffff"
                color="#003049"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <StatCard
                icon={<WarningAmberRounded />}
                title="Out of Stock"
                value={summary.outOfStock}
                helper="Needs urgent request"
                bg="#fff7ed"
                color="#ea580c"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <StatCard
                icon={<FilterAltOutlined />}
                title="Low Stock Items"
                value={summary.lowStock}
                helper="Available but critical"
                bg="#f0fdf4"
                color="#16a34a"
              />
            </Grid>
          </Grid>

          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "#fff",
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155", mb: 0.8 }}>
                    Category
                  </Typography>

                  <FormControl fullWidth>
                    <Select
                      displayEmpty
                      value={selectedCategory}
                      onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        setCurrentPage(1);
                      }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return <Typography sx={{ color: "#0f172a", fontWeight: 700 }}>All</Typography>;
                        }

                        const found = uniqueCategories.find((c) => c?.mainCategory?._id === selected);
                        return found?.mainCategory?.name || "Selected Category";
                      }}
                      sx={{
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                        fontWeight: 700,
                      }}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>

                      {uniqueCategories?.map((c) => (
                        <MenuItem key={c?.mainCategory?._id} value={c?.mainCategory?._id}>
                          {c?.mainCategory?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#334155", mb: 0.8 }}>
                    Search Raw Material
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Search by name, category, unit or remark"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                      setCurrentPage(1);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        height: 52,
                        background: "#fff",
                      },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <Button
                      variant="contained"
                      disabled={loading}
                      onClick={getStockTransactions}
                      sx={{
                        height: 52,
                        flex: 1,
                        borderRadius: 2,
                        fontWeight: 900,
                        textTransform: "none",
                        backgroundColor: "#003049",
                        "&:hover": { backgroundColor: "#00263a" },
                      }}
                    >
                      {loading ? "Loading..." : "Apply Filters"}
                    </Button>

                    <Tooltip title="Reset filters">
                      <span>
                        <Button
                          variant="outlined"
                          disabled={loading || (!selectedCategory && !searchText)}
                          onClick={handleResetFilters}
                          sx={{
                            height: 52,
                            minWidth: 52,
                            borderRadius: 2,
                            color: "#003049",
                            borderColor: "#cbd5e1",
                          }}
                        >
                          <RestartAltRounded />
                        </Button>
                      </span>
                    </Tooltip>

                    <Tooltip title="Download PDF">
                      <span>
                        <Button
                          variant="outlined"
                          disabled={loading || filteredRows.length === 0}
                          onClick={generatePDF}
                          sx={{
                            height: 52,
                            minWidth: 52,
                            borderRadius: 2,
                            color: "#003049",
                            borderColor: "#cbd5e1",
                          }}
                        >
                          <CloudDownloadOutlined />
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ py: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <CircularProgress size={28} />
                  <Typography sx={{ color: "#334155", fontWeight: 800 }}>
                    Fetching critical stock report...
                  </Typography>
                </Box>
              ) : filteredRows?.length === 0 ? (
                <Box sx={{ py: 8, px: 2, textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      mx: "auto",
                      mb: 2,
                      borderRadius: "50%",
                      backgroundColor: "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#64748b",
                    }}
                  >
                    <Inventory2Outlined sx={{ fontSize: 34 }} />
                  </Box>

                  <Typography sx={{ fontSize: 17, fontWeight: 900, color: "#0f172a" }}>
                    No critical stock items found
                  </Typography>

                  <Typography sx={{ mt: 0.7, fontSize: 13, color: "#64748b" }}>
                    Try selecting All category or clearing your search filters.
                  </Typography>

                  <Button
                    variant="contained"
                    onClick={handleResetFilters}
                    disabled={!selectedCategory && !searchText}
                    sx={{
                      mt: 2,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 800,
                      backgroundColor: "#003049",
                      "&:hover": { backgroundColor: "#00263a" },
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              ) : (
                <>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                          icon={<FilterAltOutlined />}
                          label={`Rows: ${summary.total}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />

                        <Chip
                          label={`Category: ${selectedCategoryName}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />

                        {searchText ? (
                          <Chip
                            label={`Search: ${searchText}`}
                            onDelete={() => setSearchText("")}
                            sx={{ fontWeight: 800, borderRadius: 2 }}
                          />
                        ) : null}
                      </Stack>
                    </Stack>
                  </Box>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: 0,
                      maxHeight: "70vh",
                      overflow: "auto",
                    }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["No", "Raw Material", "Stock", "Unit", "Status"].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 900,
                                color: "#0f172a",
                                background: "#f1f5f9",
                                borderBottom: "1px solid #e2e8f0",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                              }}
                              align={h === "Raw Material" ? "left" : "center"}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {paginatedRows.map((m, index) => {
                          const stock = Number(m?.inStock || 0);
                          const isOut = stock <= 0;

                          return (
                            <TableRow
                              key={(m?.rawMaterial?._id || index) + "-" + index}
                              hover
                              sx={{
                                backgroundColor: isOut
                                  ? "#fff1f2"
                                  : index % 2 === 0
                                  ? "#ffffff"
                                  : "#f8fafc",
                                "&:hover": {
                                  backgroundColor: isOut ? "#ffe4e6 !important" : "#eef6ff !important",
                                },
                                "& td": { borderBottom: "1px solid #f1f5f9" },
                              }}
                            >
                              <TableCell align="center" sx={{ fontWeight: 900, color: "#0f172a" }}>
                                {(currentPage - 1) * itemsPerPage + index + 1}
                              </TableCell>

                              <TableCell align="left">
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    fontWeight: 900,
                                    color: "#0f172a",
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {m?.rawMaterial?.name || "N/A"}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: "#64748b",
                                    textTransform: "capitalize",
                                    mt: 0.2,
                                  }}
                                >
                                  {m?.rawMaterial?.mainCategory?.name || "N/A"} &gt;{" "}
                                  {m?.rawMaterial?.subCategory?.name || "N/A"}
                                </Typography>

                                {m?.remark ? (
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      color: "#475569",
                                      mt: 0.4,
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {m.remark}
                                  </Typography>
                                ) : null}
                              </TableCell>

                              <TableCell
                                align="center"
                                sx={{
                                  fontWeight: 900,
                                  color: isOut ? "#b91c1c" : "#0f172a",
                                }}
                              >
                                {stock.toFixed(2)}
                              </TableCell>

                              <TableCell align="center" sx={{ fontWeight: 800 }}>
                                {m?.rawMaterial?.unit || "N/A"}
                              </TableCell>

                              <TableCell align="center">
                                <Chip
                                  label={isOut ? "Out of Stock" : "Low Stock"}
                                  size="small"
                                  sx={{
                                    fontWeight: 900,
                                    borderRadius: 2,
                                    backgroundColor: isOut ? "#ef4444" : "#f97316",
                                    color: "#fff",
                                    "& .MuiChip-label": { color: "#fff" },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider />

                  <Box
                    sx={{
                      py: 2,
                      px: 2,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CriticalStockReport;